import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Sparkles, Gift, Users, CheckCircle, Settings } from "lucide-react";

type ProfessionalType = "trainer" | "nutritionist" | "physiotherapist";

interface ProfessionalPromotionBannerProps {
  type?: ProfessionalType;
  isAuthenticated?: boolean;
  hasProfile?: boolean;
  compact?: boolean;
}

const CONFIG = {
  trainer: {
    landingPath: "/become-trainer",
    dashboardPath: "/dashboard/reservas",
    editPath: "/edit-trainer-profile",
    label: "Entrenador",
    color: "primary",
  },
  nutritionist: {
    landingPath: "/become-nutritionist",
    dashboardPath: "/dashboard/nutricionista/reservas",
    editPath: "/edit-nutritionist-profile",
    label: "Nutricionista",
    color: "emerald-500",
  },
  physiotherapist: {
    landingPath: "/become-physiotherapist",
    dashboardPath: "/dashboard/fisioterapeuta/reservas",
    editPath: "/edit-physiotherapist-profile",
    label: "Fisioterapeuta",
    color: "rose-500",
  },
};

export const ProfessionalPromotionBanner = ({
  type = "trainer",
  isAuthenticated = false,
  hasProfile = false,
  compact = false,
}: ProfessionalPromotionBannerProps) => {
  const navigate = useNavigate();
  const config = CONFIG[type];

  // User has a profile - show management options
  if (isAuthenticated && hasProfile) {
    return (
      <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-background">
        <CardHeader className="pb-2">
          <Badge variant="secondary" className="w-fit mb-2">
            <Settings className="w-3 h-3 mr-1" />
            Tu Perfil
          </Badge>
          <CardTitle className="text-lg">Gestiona tu Perfil</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(config.editPath)}
            >
              Editar Perfil
            </Button>
            <Button
              size="sm"
              onClick={() => navigate(config.dashboardPath)}
            >
              Mis Reservas
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact version for sidebars
  if (compact) {
    return (
      <Card className="border-2 border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-background overflow-hidden relative">
        <div className="absolute top-0 right-0 w-24 h-24 bg-primary/20 rounded-full -mr-12 -mt-12 blur-xl" />
        
        <CardContent className="relative pt-4 space-y-3">
          <Badge className="bg-primary text-primary-foreground animate-pulse">
            <Sparkles className="w-3 h-3 mr-1" />
            90 Días Gratis
          </Badge>
          
          <h3 className="font-bold">Sistema de Reservas</h3>
          <p className="text-xs text-muted-foreground">
            Gestiona citas online con pagos integrados
          </p>
          
          <Button 
            className="w-full" 
            size="sm"
            onClick={() => navigate(config.landingPath)}
          >
            Empezar Gratis
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Full promotion banner
  return (
    <div className="space-y-4">
      {/* Booking System */}
      <Card className="border-2 border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-background overflow-hidden relative">
        <div className="absolute top-0 right-0 w-40 h-40 bg-primary/20 rounded-full -mr-20 -mt-20 blur-2xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/15 rounded-full -ml-16 -mb-16 blur-xl" />
        
        <CardHeader className="relative pb-2">
          <Badge className="w-fit mb-2 bg-primary text-primary-foreground animate-pulse">
            <Sparkles className="w-3 h-3 mr-1" />
            90 Días Gratis
          </Badge>
          <CardTitle className="text-xl">Sistema de Reservas</CardTitle>
        </CardHeader>
        
        <CardContent className="relative space-y-3">
          <p className="text-sm text-muted-foreground">
            Gestiona citas online con pagos integrados. ¡Sin comisiones!
          </p>
          
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-primary" />
              <span>Calendario inteligente</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-primary" />
              <span>Recordatorios automáticos</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-primary" />
              <span>Stripe & PayPal integrado</span>
            </div>
          </div>

          <Button 
            className="w-full" 
            size="lg"
            onClick={() => navigate(config.landingPath)}
          >
            Empezar Gratis
          </Button>
        </CardContent>
      </Card>

      {/* Free Profiles */}
      <Card className="border border-accent/30 bg-gradient-to-br from-accent/10 to-background overflow-hidden relative">
        <div className="absolute top-0 right-0 w-24 h-24 bg-accent/10 rounded-full -mr-12 -mt-12" />
        
        <CardHeader className="relative pb-2">
          <Badge variant="outline" className="w-fit mb-2 border-accent/50">
            <Gift className="w-3 h-3 mr-1" />
            Perfil Gratuito
          </Badge>
          <CardTitle className="text-lg">¿Eres Profesional?</CardTitle>
        </CardHeader>
        
        <CardContent className="relative space-y-3">
          <p className="text-sm text-muted-foreground">
            Crea tu perfil gratis como entrenador, nutricionista o fisioterapeuta.
          </p>
          
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="text-xs">Entrenadores</Badge>
            <Badge variant="secondary" className="text-xs">Nutricionistas</Badge>
            <Badge variant="secondary" className="text-xs">Fisioterapeutas</Badge>
          </div>

          <Button 
            variant="outline"
            className="w-full border-accent/50 hover:bg-accent/10" 
            onClick={() => navigate('/submit-listing')}
          >
            <Users className="w-4 h-4 mr-2" />
            Registrar Perfil
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
